"use client"

import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Square, Trash2, Copy, Check, MousePointerClick } from "lucide-react"
import { useState, useCallback } from 'react'
import { useMapInteraction } from '@/contexts/MapInteractionContext'

export function MapInteractionPanel() {
  const {
    mode,
    selectedReachId,
    isSearchingReachId,
    canFetchForecast,
    roiBounds,
    showForecastTable,
    setShowForecastTable,
    activateReachIdMode,
    activateDrawRoiMode,
    deactivateMode,
    clearReachId,
    clearRoi
  } = useMapInteraction()

  const [copiedCoords, setCopiedCoords] = useState(false)

  // Copy coordinates to clipboard
  const copyCoordinates = useCallback(() => {
    if (roiBounds) {
      const coordObj = {
        west: parseFloat(roiBounds.west.toFixed(2)),
        east: parseFloat(roiBounds.east.toFixed(2)),
        north: parseFloat(roiBounds.north.toFixed(2)),
        south: parseFloat(roiBounds.south.toFixed(2))
      }
      navigator.clipboard.writeText(JSON.stringify(coordObj, null, 2))
      setCopiedCoords(true)
      setTimeout(() => setCopiedCoords(false), 2000)
    }
  }, [roiBounds])

  return (
    <>
      {/* Map Interaction Mode Selector */}
      <Card className="p-3 bg-background/90 backdrop-blur-sm shadow-lg">
        <h4 className="text-sm font-semibold mb-2">Map Interaction</h4>
        <div className="space-y-2">
          <Button
            size="sm"
            variant={mode === 'reach-id' ? "default" : "outline"}
            onClick={() => mode === 'reach-id' ? deactivateMode() : activateReachIdMode()}
            className="w-full justify-start"
          >
            <MousePointerClick className="h-4 w-4 mr-2" />
            Select Stream ID
          </Button>
          <Button
            size="sm"
            variant={mode === 'draw-roi' ? "default" : "outline"}
            onClick={() => mode === 'draw-roi' ? deactivateMode() : activateDrawRoiMode()}
            className="w-full justify-start"
          >
            <Square className="h-4 w-4 mr-2" />
            Draw ROI
          </Button>
          {mode !== 'none' && (
            <p className="text-xs text-muted-foreground mt-1">
              {mode === 'reach-id' 
                ? 'Click on map to find stream id' 
                : 'Click and drag to draw rectangle'}
            </p>
          )}
        </div>
      </Card>

      {/* ROI Bounding Box Display */}
      {roiBounds && (
        <Card className="p-3 bg-background/90 backdrop-blur-sm shadow-lg max-w-xs">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-semibold">ROI Bounding Box</h4>
            <div className="flex gap-1">
              <Button
                size="sm"
                variant="ghost"
                onClick={copyCoordinates}
                className="h-6 w-6 p-0"
                title="Copy coordinates"
              >
                {copiedCoords ? (
                  <Check className="h-3 w-3 text-green-500" />
                ) : (
                  <Copy className="h-3 w-3" />
                )}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={clearRoi}
                className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                title="Clear ROI"
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          </div>
          <div className="space-y-1 text-xs font-mono">
            <div className="grid grid-cols-2 gap-x-3">
              <span className="text-muted-foreground">North:</span>
              <span className="font-medium">{roiBounds.north.toFixed(6)}°</span>
            </div>
            <div className="grid grid-cols-2 gap-x-3">
              <span className="text-muted-foreground">South:</span>
              <span className="font-medium">{roiBounds.south.toFixed(6)}°</span>
            </div>
            <div className="grid grid-cols-2 gap-x-3">
              <span className="text-muted-foreground">East:</span>
              <span className="font-medium">{roiBounds.east.toFixed(6)}°</span>
            </div>
            <div className="grid grid-cols-2 gap-x-3">
              <span className="text-muted-foreground">West:</span>
              <span className="font-medium">{roiBounds.west.toFixed(6)}°</span>
            </div>
          </div>
          <div className="mt-2 pt-2 border-t border-border text-xs text-muted-foreground">
            <div>Width: {((roiBounds.east - roiBounds.west) * 111.32 * Math.cos(((roiBounds.north + roiBounds.south) / 2) * Math.PI / 180)).toFixed(2)} km</div>
            <div>Height: {((roiBounds.north - roiBounds.south) * 110.574).toFixed(2)} km</div>
          </div>
        </Card>
      )}

      {/* Stream ID Status - only show when in reach-id mode or has selected reach */}
      {(mode === 'reach-id' || selectedReachId) && (
        <Card className="p-3 bg-background/90 backdrop-blur-sm shadow-lg max-w-xs">
          {mode === 'reach-id' && !selectedReachId && !isSearchingReachId && (
            <p className="text-sm text-muted-foreground">Click on the map to find the nearest stream.</p>
          )}
          {isSearchingReachId && (
            <div className="flex items-center gap-3">
              <div className="animate-spin h-5 w-5 border-2 border-primary border-t-transparent rounded-full"></div>
              <div>
                <p className="font-semibold text-sm">Finding Stream ID...</p>
                <p className="text-xs text-muted-foreground">Searching river data...</p>
              </div>
            </div>
          )}
          {canFetchForecast && selectedReachId && !showForecastTable && (
            <div className="text-center space-y-3">
              <div className="flex items-center justify-between">
                <div className="text-left">
                  <h4 className="font-semibold">Stream Found!</h4>
                  <p className="text-sm text-muted-foreground">ID: {selectedReachId}</p>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={clearReachId}
                  className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                  title="Clear selection"
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
              <Button
                onClick={() => setShowForecastTable(true)}
                className="w-full"
                size="sm"
              >
                📊 Fetch Forecast
              </Button>
            </div>
          )}
        </Card>
      )}
    </>
  )
}
